import { FC, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { useErrorHandler, ErrorFieldType, isPermitted, useJWT } from 'src/app/common/utils';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { AdminCommissionToolPermissionType } from 'src/app/modules/CommissionTool/constants';
import { map } from 'lodash';
import { MANDATORY_FIELD_ERROR_TEXT } from 'src/app/common/constants';
import {
  MasterDataFormMode,
  MasterDataSettingListItem,
  MASTER_DATA_VALIDATION,
  MAX_VALUES_PER_CREATION,
  MAX_VALUES_PER_MASTER_DATA,
  ProductField,
} from '../../../../types/master-data-setting-types';
import {
  formReducer,
  initFormState,
  getDuplicateIndexes as getDuplicateIndexesUtil,
  validateValues as validateValuesUtil,
  sanitizeValueInput,
} from '../../../../utils/master-data-validation';
import { createMasterDataSettingItem, modifyMasterDataSettingItem } from '../../../../network/master-data-setting-crud';
import { masterDataSettingPath } from '../../master-data-setting-routes';
import { MasterDataNameOptions, LanguageKey } from 'src/app/modules/CommissionTool/constants';
import { useTranslation } from 'src/app/common/hook/translation.hook';

type MasterDataSettingFormProps = {
  formMode: MasterDataFormMode;
  masterDataItem?: MasterDataSettingListItem;
};

const FIELD_CONTAINER_WIDTH = 180;

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  rowSpacing: {
    marginBottom: 16,
  },
  fieldContainer: {
    width: FIELD_CONTAINER_WIDTH,
    boxSizing: 'border-box',
  },
  field: {
    fontSize: '1rem',
  },
  mandatory: {
    color: 'red',
  },
  sectionMargin: {
    marginBottom: 15,
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
  },
  valueRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 8,
  },
  valueInputContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  deleteButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '56px',
    justifyContent: 'center',
  },
}));

const MasterDataSettingForm: FC<MasterDataSettingFormProps> = ({ formMode, masterDataItem }) => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const Translation = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [formState, formDispatch] = useReducer(formReducer, initFormState(masterDataItem));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  let updatePermission = false;
  const jwt = useJWT() || {};

  if (jwt.permissions) {
    const jwtPermission = map(jwt.permissions, (_, key) => key);
    updatePermission = isPermitted([AdminCommissionToolPermissionType.MASTER_DATA_SETTING_UPDATE], jwtPermission);
  }

  if (!updatePermission) {
    history.push(`${masterDataSettingPath}/list`);
  }

  const { errorState, onSubmitErrorValidator, onDismissErrorHandler } = useErrorHandler(formState, [
    {
      name: 'name',
      fieldType: ErrorFieldType.MANDATORY,
    },
  ]);

  const isCreateMode = formMode === MasterDataFormMode.CREATE;
  const isViewMode = formMode === MasterDataFormMode.VIEW;
  const validation = formState.name
    ? MASTER_DATA_VALIDATION[formState.name]
    : MASTER_DATA_VALIDATION[ProductField.PRODUCT_NAME];
  const existingValues = masterDataItem ? masterDataItem.values.map((v) => v.value) : [];
  const newValuesCount = formState.values.filter((v) => !v._id).length;
  const canAddMore = formState.values.length < MAX_VALUES_PER_MASTER_DATA && newValuesCount < MAX_VALUES_PER_CREATION;

  const getDuplicateIndexes = (): number[] => getDuplicateIndexesUtil(formState.values, isCreateMode, existingValues);

  const validateValues = (): boolean => validateValuesUtil(formState.values, isCreateMode, existingValues);

  const handleValueChange = (index: number, rawValue: string) => {
    const value = sanitizeValueInput(rawValue, validation);
    formDispatch({ type: 'SET_VALUE', payload: { index, value } });
  };

  const onSubmitConfirm = () => {
    setIsSubmitAttempted(true);
    const { hasError } = onSubmitErrorValidator();
    if (hasError || !validateValues()) return;
    setConfirmOpen(true);
  };

  const onSubmit = async () => {
    setConfirmOpen(false);
    try {
      if (isCreateMode) {
        await createMasterDataSettingItem(
          {
            name: formState.name as ProductField,
            values: formState.values.map((v) => v.value),
          },
          dispatch,
        );
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: Translation('master.data.setting.update.success', {
                name: Translation(LanguageKey[formState.name as ProductField]) || '',
              }),
            },
          ]),
        );
      } else if (masterDataItem) {
        await modifyMasterDataSettingItem(
          masterDataItem._id,
          {
            values: formState.values.map((v) => v.value),
          },
          dispatch,
        );
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: Translation('master.data.setting.update.success', {
                name: Translation(LanguageKey[formState.name as ProductField]) || '',
              }),
            },
          ]),
        );
      }
      history.push(`${masterDataSettingPath}/list`);
    } catch (err) {}
  };

  return (
    <>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle align="center">{Translation('contract.setting.confirm')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{Translation('contract.setting.submit.confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" onClick={() => setConfirmOpen(false)}>
            {Translation('app.button.cancel')}
          </Button>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            {Translation('app.button.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={classes.rowContainer}>
            <div className={commonClasses.header}>
              {Translation(
                isCreateMode
                  ? 'master.data.setting.create'
                  : isViewMode
                  ? 'master.data.setting.view'
                  : 'master.data.setting.edit',
              )}
            </div>
          </div>
          <Button variant="contained" color="inherit" onClick={() => history.push(`${masterDataSettingPath}/list`)}>
            {Translation('app.button.back')}
          </Button>
        </div>

        <div className={classes.sectionMargin}>
          {/* Master Data Name */}
          <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {Translation('master.data.setting.name')}
                <span className={classes.mandatory}>*</span>
              </span>
            </div>
            <div style={{ flexGrow: 1 }}>
              <FormControl
                error={errorState.mandatory.name}
                margin="dense"
                variant="outlined"
                fullWidth
                style={{ minWidth: 300 }}
              >
                <Select
                  placeholder={Translation('master.data.setting.pleaseSelect')}
                  displayEmpty
                  value={formState.name || ''}
                  disabled={!isCreateMode}
                  onChange={(e) => {
                    if (isViewMode) return;
                    const name = e.target.value as ProductField;
                    onDismissErrorHandler('name', name);
                  }}
                >
                  {MasterDataNameOptions.map((options) => (
                    <MenuItem key={options.value} value={options.value}>
                      {Translation(options.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
                {errorState.mandatory.name && (
                  <FormHelperText style={{ color: '#f018a6' }}>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>

          {/* Values */}
          <div className={`${classes.rowContainer} ${classes.rowSpacing}`} style={{ alignItems: 'flex-start' }}>
            <div className={classes.fieldContainer} style={{ paddingTop: 12 }}>
              <span className={classes.field}>
                {Translation('master.data.setting.value')}
                <span className={classes.mandatory}>*</span>
              </span>
            </div>
            <div style={{ flexGrow: 1 }}>
              {(() => {
                const duplicateIdxs = isSubmitAttempted ? getDuplicateIndexes() : [];
                return formState.values.map((item, index) => (
                  <div key={item._id ?? index} className={classes.valueRow}>
                    <div className={classes.valueInputContainer}>
                      <TextField
                        placeholder={Translation('master.data.setting.pleaseInput')}
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        style={{ minWidth: 300 }}
                        value={item.value}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        disabled={isViewMode}
                        error={isSubmitAttempted && (item.value.trim().length === 0 || duplicateIdxs.includes(index))}
                        helperText={
                          isSubmitAttempted && item.value.trim().length === 0
                            ? MANDATORY_FIELD_ERROR_TEXT
                            : duplicateIdxs.includes(index)
                            ? Translation('master.data.setting.value.duplicate')
                            : ''
                        }
                        type="text"
                        inputProps={
                          validation?.type === 'number'
                            ? { maxLength: validation?.maxLength, inputMode: 'numeric', pattern: '[0-9]*' }
                            : {
                                maxLength: validation?.maxLength,
                              }
                        }
                      />
                    </div>
                    {!isViewMode && formState.values.length > 1 && (
                      <div className={classes.deleteButtonContainer}>
                        <IconButton size="small" onClick={() => formDispatch({ type: 'REMOVE_VALUE', payload: index })}>
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    )}
                  </div>
                ));
              })()}
              {!isViewMode && canAddMore && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => formDispatch({ type: 'ADD_VALUE' })}
                  style={{ marginTop: 4 }}
                >
                  {Translation('master.data.setting.addValue')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {!isViewMode && (
          <div className={classes.footerContainer}>
            <Button variant="contained" color="secondary" onClick={onSubmitConfirm}>
              {Translation('app.button.submit')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default MasterDataSettingForm;
